/**
 * useTaskList — TanStack Query hook for the Aufgaben queue (GET /tasks).
 *
 * live mode → apiClient.tasks.list (via fetchTaskRows) with include_total for
 *             the "x von y" count. Server-side filters (status/process/
 *             assigned_to/q) are forwarded as params + carried in the query key
 *             so a change refetches.
 * mock mode → resolves AUFGABEN_MOCK synchronously (mock layer NOT deleted); the
 *             screen applies the saved-view presets + search client-side over
 *             these rows, so staleTime:Infinity is safe.
 *
 * One typed query hook per resource (frontend-architecture §3). Server state
 * only — never mirrored into useState.
 *
 * [Source: apps/web/src/features/invoices/hooks/useInvoiceRows.ts (hook pattern)]
 * [Source: apps/web/src/features/communication/hooks/useInbox.ts (GET /tasks)]
 */

import { useQuery } from '@tanstack/react-query';
import { qk } from '../../../lib/query-keys';
import { isLiveData } from '../../../lib/data-mode';
import { fetchTaskRows } from '../api/aufgaben-api';
import type { TaskListParams } from '../api/aufgaben-api';
import { AUFGABEN_MOCK } from '../mock';
import type { TaskRow } from '../types';

export interface TaskListResult {
  rows: TaskRow[];
  /** Server total across all pages (include_total); null when the wire omits it. */
  total: number | null;
}

const MOCK_RESULT: TaskListResult = { rows: AUFGABEN_MOCK, total: AUFGABEN_MOCK.length };

export function useTaskList(params: TaskListParams = {}) {
  const live = isLiveData();
  return useQuery<TaskListResult>({
    // `aufgaben` scope segment keeps this key distinct from the Eingang inbox
    // query (same GET /tasks endpoint, different screen + params).
    queryKey: qk.inbox({ aufgaben: true, ...params }),
    queryFn: () => (live ? fetchTaskRows(params) : Promise.resolve(MOCK_RESULT)),
    ...(live ? {} : { initialData: MOCK_RESULT, staleTime: Infinity }),
  });
}
